import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateSiteMutation, useGetSitesQuery, useGetSiteTemplatesQuery } from "@/store/rest_api";
import { useAddOn } from "@/hooks/useAddOn";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExternalLink, Globe } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/util/apiError";
import { PageTemplate } from "@/model/interface/Site";
import { AddOnType } from "@/enum/AddOnType";

export const ADD_ONS_PATH = "/manage-organisation/billing/add-ons";

export function sitePublicUrl(slug: string): string {
  const isLocal = window.location.hostname.includes("localhost");
  return isLocal ? `https://${slug}.localhost:8443` : `https://${slug}.onretreevo.com`;
}

function NameSiteDialog({ template, onClose }: { template: PageTemplate | null; onClose: () => void }) {
  const [createSite, { isLoading }] = useCreateSiteMutation();
  const navigate = useNavigate();
  const [name, setName] = useState("");

  const submit = async () => {
    if (!template || !name.trim()) {
      return;
    }
    try {
      const site = await createSite({ sourceTemplateKey: template.templateKey, name: name.trim() }).unwrap();
      toast.success("Website created");
      onClose();
      setName("");
      navigate(`/sites/${site.id}`);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to create website"));
    }
  };

  return (
    <Dialog open={template !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Name your website</DialogTitle>
          <DialogDescription>Using the {template?.name} template. You can customize everything next.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="site-name">Website name</Label>
          <Input
            id="site-name"
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. Austin Cash Offers"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || isLoading}>
            Create website
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SitesPage() {
  const { data: sites } = useGetSitesQuery();
  const { data: templates } = useGetSiteTemplatesQuery();
  const hasAddOn = useAddOn(AddOnType.WEBSITE_BUILDER);
  const navigate = useNavigate();

  const [nameTemplate, setNameTemplate] = useState<PageTemplate | null>(null);

  const hasSites = (sites?.length ?? 0) > 0;

  return (
    <div className="container p-4 space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Websites</h1>
        <p className="text-muted-foreground">SEO lead-capture sites that feed leads straight into your CRM.</p>
      </div>

      {!hasAddOn && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-5">
            <div>
              <p className="font-semibold">Build for free, publish for $49/mo</p>
              <p className="text-sm text-muted-foreground">
                Create and preview sites at no cost. The Website Builder add-on is only needed to publish them live.
              </p>
            </div>
            <Button onClick={() => navigate(ADD_ONS_PATH)}>Get Website Builder</Button>
          </CardContent>
        </Card>
      )}

      {hasSites && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Your websites</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
            {sites?.map((site) => (
              <Card key={site.id} className="flex flex-col">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                      <Globe className="h-5 w-5 text-primary" />
                    </div>
                    <Badge variant={site.status === "PUBLISHED" ? "default" : "secondary"}>
                      {site.status === "PUBLISHED" ? "Published" : "Draft"}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg mt-3">{site.name}</CardTitle>
                  <CardDescription>{site.slug}.onretreevo.com</CardDescription>
                </CardHeader>
                <CardContent className="flex-1" />
                <CardFooter className="gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => navigate(`/sites/${site.id}`)}>
                    Edit
                  </Button>
                  {site.status === "PUBLISHED" && (
                    <Button variant="ghost" size="icon" asChild>
                      <a href={sitePublicUrl(site.slug)} target="_blank" rel="noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        <h2 className="text-lg font-semibold">{hasSites ? "Start a new site from a template" : "Choose a template"}</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
          {templates?.map((template) => (
            <Card key={template.id} className="flex flex-col">
              <CardHeader>
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Globe className="h-5 w-5 text-primary" />
                </div>
                <CardTitle className="text-lg mt-3">{template.name}</CardTitle>
                <CardDescription>{template.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1" />
              <CardFooter>
                <Button className="w-full" onClick={() => setNameTemplate(template)}>
                  Use template
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>

      <NameSiteDialog template={nameTemplate} onClose={() => setNameTemplate(null)} />
    </div>
  );
}
